/* @flow */
import idx from 'idx';
import uuid from 'uuid';
import * as React from 'react';
import buildURL from 'lib/buildURL';
import Row from 'components/Row';
import * as storage from 'lib/storage';
import Text from 'components/Text';
import Link from 'components/Link';
import assertions from 'lib/assertions';
import Image from 'components/Image';
import Input from 'components/Input';
import Button from 'components/Button';
import Heading from 'components/Heading';
import Columns2 from 'components/Columns2';
import ToastActions from 'actions/ToastActions';
import { withRouter } from 'app/context/Router';
import FormElement from 'components/FormElement';
import WizardHeader from 'components/WizardHeader';
import WizardFooter from 'components/WizardFooter';
import ClipboardTrigger from 'components/ClipboardTrigger';
import IntegrationWizardHeaderContent from 'admin/components/IntegrationWizardHeaderContent';

import './styles/CanvasACI.scss';

function Step(props: { title: string, children: React.Node }) {
  return (
    <Row size="large">
      <Row size="small">
        <Text size="xlarge" element="div">
          {props.title}
        </Text>
      </Row>
      {props.children}
    </Row>
  );
}

type Props = {
  router: any,
  integration: any,
  upsert: Function,
  onDone: Function,
  organizationUUID: string,
};

type State = {
  clientID?: ?string,
  clientSecret?: ?string,
  enabledByOrg?: ?boolean,
  installationURLDomain?: ?string,
};

const b = str => <Text weight="bold">{str}</Text>;
const removeProtocol = str => (str ? str.replace(/(^\w+:|^)\/\//, '') : str);

class CanvasACI extends React.Component<Props, State> {
  state: State = {
    clientID: '',
    clientSecret: '',
    enabledByOrg: true,
    installationURLDomain: '',
  };

  componentDidMount() {
    this.setState({
      enabledByOrg: idx(this.props, _ => _.integration.isACIIntegrated),
      clientID: idx(
        this.props,
        _ => _.integration.adminCustomizedIntegrationSetup.integrationOauthConfig.clientId
      ),
      clientSecret: idx(
        this.props,
        _ => _.integration.adminCustomizedIntegrationSetup.integrationOauthConfig.clientSecret
      ),
      installationURLDomain: idx(
        this.props,
        _ => _.integration.adminCustomizedIntegrationSetup.installationURLDomain
      ),
    });
  }

  submit = (key: ?string) => {
    this.props
      .upsert({
        initialIntegrationKey: key,
        enabledByOrg: this.state.enabledByOrg,
        config: {
          clientID: this.state.clientID,
          clientSecret: this.state.clientSecret,
          installationURLDomain: removeProtocol(this.state.installationURLDomain),
        },
      })
      .then(response => {
        const error = idx(response, _ => _.data.upsertAdminControlledIntegration.error.message);

        if (error) {
          ToastActions.showErrorToast(error);
        } else if (this.state.enabledByOrg) {
          this.props.onDone();
        } else {
          this.props.router.goBack();
        }
      })
      .catch(e => {
        console.error(e);
      });
  };

  canSubmit = () => {
    const { integration } = this.props;

    const wasEnabled = !!idx(integration, _ => _.isACIIntegrated);
    const isSetup = !!idx(integration, _ => _.adminCustomizedIntegrationSetup.integrationKey);

    return (
      // If it's never been setup, or if it's enabled now, or it was toggled (and therefore changed to disabled)
      (!isSetup || this.state.enabledByOrg || this.state.enabledByOrg !== wasEnabled) &&
      this.state.clientID != null &&
      this.state.clientSecret != null &&
      this.state.installationURLDomain != null
    );
  };

  render() {
    const { integration, organizationUUID } = this.props;

    const store = storage.make(`canvas_aci:${organizationUUID}`);
    const mask = idx(integration, _ => _.adminCustomizedIntegrationSetup.redirectURIMask);
    const isSetup = !!idx(integration, _ => _.adminCustomizedIntegrationSetup.integrationKey);

    let key: ?string = idx(integration, _ => _.adminCustomizedIntegrationSetup.integrationKey);
    let uri: ?string = idx(integration, _ => _.adminCustomizedIntegrationSetup.redirectURI);

    if (mask && !key) {
      try {
        key = store.get() || store.set(uuid());
      } catch (e) {
        console.error(e);
        key = uuid();
      }

      uri = buildURL(mask, { uuid: key });
    }

    return (
      <div className="CanvasACI">
        <WizardHeader>
          <IntegrationWizardHeaderContent
            title="Canvas Setup"
            iconURL={this.props.integration.iconURL}
          />
        </WizardHeader>
        <div className="CanvasACI-content">
          <Heading level="1">Using Canvas Cloud</Heading>
          {isSetup &&
            this.state.enabledByOrg && (
              <Row>
                <Button
                  onClick={() => this.setState({ enabledByOrg: false })}
                  level="secondary"
                  color="lava">
                  Disable
                </Button>
              </Row>
            )}
          {isSetup &&
            !this.state.enabledByOrg && (
              <Row>
                <Button onClick={() => this.setState({ enabledByOrg: true })}>Enable</Button>
              </Row>
            )}
          <div className="CanvasACI-content-step1">
            <div className="CanvasACI-content-step1-help">
              <Step title="1. Log in to your Canvas Account in a new tab or window to complete the following:">
                <Row>
                  <ol className="CanvasACI-content-substeps">
                    <li>1. In Canvas, go to the {b`Admin`} Tab.</li>
                    <li>2. Click on the name of the org you want to configure Canvas for </li>
                    <li>3. Go to the {b`Developer Keys`} Tab.</li>
                    <li>4. Click the + {b`Developer Key`} button</li>
                    <li>5. Add a value (such as Remind) in the {b`Key Name`} field.</li>
                    <li>
                      6. Enter the email address of the account owner in the {b`Owner Email`} field.
                    </li>
                    <li>7. Leave the {b`Redirect_URI (Legacy)`} field blank.</li>
                    <li>
                      8. Set {b`Redirect_URI`} to the value to{' '}
                      {uri && (
                        <ClipboardTrigger
                          text={uri}
                          onCopy={() => ToastActions.showSuccessToast('Copied!')}>
                          <Link href="#">{uri}</Link>
                        </ClipboardTrigger>
                      )}
                    </li>
                    <li>9. Click {b`Save Key`}. Canvas will then generate a new Developer Key.</li>
                    <li>
                      10. Once you have obtained the {b`ID`} and {b`Key`},{' '}
                      {b`return to this window and
                  enter the ID and key below.`}
                    </li>
                  </ol>
                </Row>
                <div className="CanvasACI-content-images">
                  <Image src={require('assets/images/canvas_setup_1.svg')} />
                  <Image src={require('assets/images/canvas_setup_2.svg')} />
                </div>
              </Step>
            </div>
            <div className="CanvasACI-content-step1-note">
              <Text className="CanvasACI-content-step1-note-text" element="div">
                {b`Note:`} Make sure to keep this window open while you access Canvas in another tab
                so that you can refer back to the directions.
              </Text>
            </div>
          </div>
          <Step title="3. Enter ID and Key">
            <Columns2>
              <FormElement
                label="ID"
                element={Input}
                validate={e => assertions.notEmpty(e.target.value)}
                onInvalidChange={() => this.setState({ clientID: null })}
                onValidChange={e => this.setState({ clientID: e.target.value })}
                elementProps={{
                  name: 'id',
                  value: this.state.clientID,
                  placeholder: '100000000000023',
                }}
              />
              <FormElement
                label="Key"
                element={Input}
                validate={e => assertions.notEmpty(e.target.value)}
                onInvalidChange={() => this.setState({ clientSecret: null })}
                onValidChange={e => this.setState({ clientSecret: e.target.value })}
                elementProps={{
                  name: 'key',
                  placeholder: 'Remind Key',
                  value: this.state.clientSecret,
                }}
              />
            </Columns2>
          </Step>
          <Step title="4. Enter the URL where your Canvas installation lives">
            <Row size="xsmall">
              <Text>
                While logged into your Canvas account, go to that browser window and copy the URL
                and paste it below. Copy the entire URL, but do not include anything after {b`.com`}
              </Text>
            </Row>
            <div className="CanvasACI-content-step4-form">
              <FormElement
                label=""
                element={Input}
                validate={e => assertions.notEmpty(e.target.value)}
                onInvalidChange={() => this.setState({ installationURLDomain: null })}
                onValidChange={e => this.setState({ installationURLDomain: e.target.value })}
                elementProps={{
                  name: 'key',
                  value: this.state.installationURLDomain,
                  placeholder: 'https://canvas.my-school.com',
                }}
              />
              <Image src={require('assets/images/canvas_setup_3.svg')} />
            </div>
          </Step>
        </div>
        <WizardFooter>
          <div className="CanvasACI-footer">
            <Button onClick={this.props.router.goBack} level="secondary">
              Back
            </Button>
            <Button disabled={!this.canSubmit()} onClick={() => this.submit(key)}>
              Submit
            </Button>
          </div>
        </WizardFooter>
      </div>
    );
  }
}

export default withRouter(CanvasACI);
